import { useState, type ReactNode } from 'react';
import { CalendarClock, CircleCheck, Eye, ListChecks, TriangleAlert } from 'lucide-react';
import { deadlineOverview, type AutoDraft, type AutoDraftResult, type DeadlineEntry, type DeadlineGroup, type PlanReview } from '@autoday/core';
import { getStore, useStore } from '../store/appStore.ts';
import { usePrepAttention } from '../prep/drafts.ts';
import { deadlineRowInfo, draftRowInfo, newPlanBlock, skippedLine, type DeadlineRowAction } from '../components/prep/prepText.ts';
import { useToday } from '../ui/hooks.ts';
import { Button, Panel } from '../ui/kit.tsx';
import { AgendaRow } from '../components/Agenda.tsx';
import { useUi } from '../App.tsx';
import { tr, useLang } from '../i18n/i18n.ts';
import { formatClock, formatDayMonth, weekdayName } from '../i18n/format.ts';
import { Temporal } from '@autoday/core';

const GROUP_TEXT: Record<Exclude<DeadlineGroup, 'done'>, { title: [string, string]; hint: [string, string]; tone: string }> = {
  review: {
    title: ['Behöver granskas', 'Needs review'],
    hint: ['Källan har ändrats eller saknades i senaste importen. Inget har raderats, flyttats eller klarmarkerats automatiskt – inte heller dina förberedelser.', 'The source has changed or was missing in the latest import. Nothing has been deleted, moved or marked done automatically – nor your preparation.'],
    tone: 'border-warn/50',
  },
  passed: {
    title: ['Datum passerat, inte markerade klara här', 'Date passed, not marked done here'],
    hint: ['Passerat betyder inte misslyckat. AutoDay vet inte om du agerat utanför appen – kontrollera och markera klar här om det stämmer.', 'Passed does not mean failed. AutoDay does not know whether you acted outside the app – check and mark it done here if that is right.'],
    tone: 'border-risk/50',
  },
  soon: { title: ['Närmar sig (7 dagar)', 'Coming up (7 days)'], hint: ['', ''], tone: '' },
  later: { title: ['Senare', 'Later'], hint: ['', ''], tone: '' },
};

/** Kort beskrivning av en planerad förberedelse: "tis 3 juni 09.00". */
export function prepWhen(p: DeadlineEntry['prep'][number]): string {
  const d = Temporal.PlainDate.from(p.date);
  const time = p.timing.kind === 'timed' ? ' ' + formatClock(Number(p.timing.start.slice(11, 13)), Number(p.timing.start.slice(14, 16))) : '';
  return `${weekdayName(d.dayOfWeek, true)} ${formatDayMonth(p.date)}${time}`;
}

const ROW_ACTIONS: Record<Exclude<DeadlineRowAction, null>, { label: [string, string]; icon: ReactNode; intent?: 'prep' | 'plan' }> = {
  review: { label: ['Granska', 'Review'], icon: <Eye size={16} aria-hidden /> },
  confirm: { label: ['Kontrollera', 'Check'], icon: <CircleCheck size={16} aria-hidden /> },
  plan: { label: ['Planera förberedelser', 'Plan preparation'], icon: <ListChecks size={16} aria-hidden />, intent: 'plan' },
  find: { label: ['Hitta tid att förbereda', 'Find time to prepare'], icon: <CalendarClock size={16} aria-hidden />, intent: 'prep' },
  'plan-review': { label: ['Se över', 'Take a look'], icon: <TriangleAlert size={16} aria-hidden />, intent: 'plan' },
};

/** En rad: förberedelsernas status och EN nästa handling (granskning före plan, plan före enstaka). */
function EntryRow({ e, today, tz, reviews, draftKeys }: { e: DeadlineEntry; today: string; tz: string; reviews: ReadonlyMap<string, PlanReview>; draftKeys: ReadonlySet<string> }) {
  const ui = useUi();
  const { state } = useStore();
  const { t } = useLang();
  const key = e.item.ref.kind === 'source' ? e.item.ref.key : null;
  const info = deadlineRowInfo(e, {
    review: key ? (reviews.get(key) ?? null) : null,
    plannable: !!key && !newPlanBlock(state, key, getStore().clock.now()),
    draft: !!key && draftKeys.has(key),
    tz,
  });
  const a = info.action ? ROW_ACTIONS[info.action] : null;
  return (
    <li className="border-b border-border last:border-b-0">
      <AgendaRow item={e.item} today={today} tz={tz} showDate bare />
      <div className="flex flex-wrap items-center gap-2 px-3 pb-3 pl-[8.75rem] text-xs text-muted max-sm:pl-3">
        <span className={`min-w-0 flex-1 ${info.action === 'plan-review' ? 'font-medium text-warn' : ''}`}>
          {info.action === 'plan-review' && <TriangleAlert size={14} aria-hidden className="mr-1 inline-block align-[-2px]" />}
          {info.text}
        </span>
        {a && (
          <Button className="min-h-10 px-3" icon={a.icon} onClick={() => ui.openItem(e.item, a.intent)}>
            {t(...a.label)}
          </Button>
        )}
      </div>
    </li>
  );
}

/** Automatiska utkast enligt användarens regler: en lugn grupp, ingen popup. Samma beräkning som Idag. */
function DraftGroup({ result, drafts }: { result: AutoDraftResult | null; drafts: AutoDraft[] }) {
  const ui = useUi();
  const { state } = useStore();
  const { t } = useLang();
  if (!result || (!drafts.length && !result.skipped.length)) return null;
  const rules = result.rules.filter((r) => r.status === 'active');
  const ruleText = rules.map((r) => `${r.sourceName} → ${r.templateName ?? '–'}`).join(', ');
  const why = (
    <details className="mx-3 mb-2 mt-1 text-xs">
      <summary className="min-h-9 cursor-pointer py-2 font-medium text-muted">{t(`Varför saknas förslag? (${result.skipped.length})`, `Why no suggestion? (${result.skipped.length})`)}</summary>
      <ul className="mt-1 space-y-1 text-muted">
        {result.skipped.map((x) => (
          <li key={x.sourceKey} className="[overflow-wrap:anywhere]">
            {skippedLine(x)}
          </li>
        ))}
      </ul>
    </details>
  );
  // Inget väntar: ingen rubrik med (0) – bara den hopfällbara förklaringen för automatiska förslag.
  if (!drafts.length)
    return (
      <Panel as="div" className="px-0 py-0">
        <p className="flex items-center gap-2 px-3 pt-2 text-xs text-muted">
          <ListChecks size={14} aria-hidden className="text-accent" /> {t('Automatiska förslag: inga väntar just nu.', 'Automatic suggestions: none waiting right now.')}
        </p>
        {why}
      </Panel>
    );
  return (
    <Panel className="p-2" aria-labelledby="dl-drafts">
      <h2 id="dl-drafts" className="flex items-center gap-2 px-3 pt-2 text-sm font-semibold text-accent">
        <ListChecks size={16} aria-hidden /> {t(`Förslag att granska (${drafts.length})`, `Suggestions to review (${drafts.length})`)}
      </h2>
      <p className="px-3 text-xs text-muted">
        {rules.length ? t(`Enligt dina regler: ${ruleText}. Inget läggs in förrän du godkänner.`, `From your rules: ${ruleText}. Nothing is added until you approve.`) : t('Inget läggs in förrän du godkänner.', 'Nothing is added until you approve.')}
      </p>
      {drafts.length > 0 ? (
        <ul className="mt-1">
          {drafts.map((d) => {
            const r = draftRowInfo(d, state);
            return (
              <li key={d.sourceKey} className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-2 last:border-b-0">
                <span className="min-w-0 flex-1">
                  <span className="block font-medium [overflow-wrap:anywhere]">
                    {r.title}
                    {r.date ? <span className="font-normal text-muted"> · {r.date}</span> : null}
                  </span>
                  <span className={`block text-xs [overflow-wrap:anywhere] ${r.fits ? 'text-muted' : 'font-medium text-warn'}`}>
                    {!r.fits && <TriangleAlert size={12} aria-hidden className="mr-1 inline-block align-[-1px]" />}
                    {r.template ? `${r.template}: ` : ''}
                    {r.times}
                  </span>
                </span>
                <Button className="min-h-10 px-3" icon={<Eye size={16} aria-hidden />} onClick={() => ui.openItem({ ref: d.sourceKey }, 'plan')}>
                  {t('Granska', 'Review')}
                  <span className="sr-only"> {r.title}</span>
                </Button>
              </li>
            );
          })}
        </ul>
      ) : null}
      {result.truncated && (
        <p className="px-3 pb-1 text-xs text-muted">{t(`Visar de ${result.drafts.length} närmaste. Fler visas när de här är hanterade.`, `Showing the ${result.drafts.length} nearest. More appear when these are handled.`)}</p>
      )}
      {result.skipped.length > 0 && why}
    </Panel>
  );
}

/**
 * Deadline Center. Samma underlag (deadlineOverview i core) som Idag och räknarna: varje förekomst finns i
 * exakt en grupp, och länkade förberedelser visas under sin deadline i stället för som egna rader.
 */
export function ImportantView() {
  const { state } = useStore();
  const ui = useUi();
  const today = useToday();
  const { t } = useLang();
  const tz = state.settings.timeZone;
  const [horizon, setHorizon] = useState(120);
  const ov = deadlineOverview(state, today, horizon);
  // Samma beräkning som Idag: automatiska utkast och granskning av godkända planer.
  const attention = usePrepAttention();
  const reviews = attention.reviewsByKey;
  const draftKeys = new Set(attention.drafts.map((d) => d.sourceKey));
  const by = (g: DeadlineGroup) => ov.entries.filter((e) => e.group === g);
  const done = by('done');
  const empty = ov.entries.length === 0;

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-5 px-4 py-5 lg:px-8">
      <header>
        <p className="mb-1 text-xs font-medium uppercase tracking-widest text-accent">{t('Deadline Center', 'Deadline Center')}</p>
        <h1 className="text-2xl font-semibold tracking-tight">{t('Viktigt', 'Deadlines')}</h1>
        <p className="text-sm text-muted">
          {t(
            'Deadlines är poster från källor som räknas som deadlines och egna aktiviteter du markerat som viktiga. Vanliga importerade möten räknas inte. Taggar kommer från importkällan; dagar kvar räknas här.',
            'Deadlines are items from sources counted as deadlines and your own activities marked as important. Ordinary imported meetings do not count. Tags come from the import source; days left are counted here.',
          )}{' '}
          <a className="font-medium text-accent underline-offset-2 hover:underline" href="#/kalendrar">
            {t('Ändra under Mina kalendrar', 'Change under My calendars')}
          </a>
        </p>
        <Button className="mt-3 min-h-10 px-3" icon={<ListChecks size={16} aria-hidden />} aria-haspopup="dialog" onClick={() => ui.openPrepPlans()}>
          {t('Förberedelseplaner', 'Preparation plans')}
        </Button>
      </header>

      <DraftGroup result={attention.result} drafts={attention.drafts} />

      <section aria-label={t('Översikt', 'Overview')} className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {(['review', 'passed', 'soon', 'later'] as const).map((g) => (
          <div key={g} className={`rounded-xl border bg-panel px-3 py-2 ${ov.counts[g] && (g === 'review' || g === 'passed') ? (g === 'review' ? 'border-warn/60' : 'border-risk/60') : 'border-border'}`}>
            <p className="text-2xl font-semibold tabular-nums">{ov.counts[g]}</p>
            <p className="text-xs text-muted">{t(...GROUP_TEXT[g].title)}</p>
          </div>
        ))}
      </section>

      {empty && (
        <Panel className="p-5">
          <p className="font-medium">{t('Inga deadlines just nu.', 'No deadlines right now.')}</p>
          <p className="mt-1 text-sm text-muted">
            {t(
              `Inget inom ${horizon} dagar. Importera en kalenderfil under Import och välj vilka källor som räknas som deadlines, eller markera en egen aktivitet med "Visa under Viktigt".`,
              `Nothing within ${horizon} days. Import a calendar file under Import and choose which sources count as deadlines, or mark one of your own activities with "Show under Deadlines".`,
            )}
          </p>
        </Panel>
      )}

      {(['review', 'passed', 'soon', 'later'] as const).map((g) => {
        const list = by(g);
        if (!list.length && (g === 'review' || g === 'passed')) return null;
        const meta = GROUP_TEXT[g];
        if (empty) return null;
        return (
          <Panel key={g} className={`p-2 ${meta.tone}`} aria-labelledby={`dl-${g}`}>
            <h2 id={`dl-${g}`} className={`flex items-center gap-2 px-3 pt-2 text-sm font-semibold ${g === 'review' ? 'text-warn' : g === 'passed' ? 'text-risk' : 'text-muted'}`}>
              {(g === 'review' || g === 'passed') && <TriangleAlert size={16} aria-hidden />}
              {t(...meta.title)} ({list.length})
            </h2>
            {meta.hint[0] && <p className="px-3 text-xs text-muted">{t(...meta.hint)}</p>}
            {list.length ? (
              <ul className="mt-1">
                {list.map((e) => (
                  <EntryRow key={e.item.uid} e={e} today={today} tz={tz} reviews={reviews} draftKeys={draftKeys} />
                ))}
              </ul>
            ) : (
              <p className="px-3 py-4 text-sm text-muted">{g === 'soon' ? t('Inget de närmaste sju dagarna.', 'Nothing in the next seven days.') : t(`Inget mer inom ${horizon} dagar.`, `Nothing more within ${horizon} days.`)}</p>
            )}
            {g === 'later' && horizon < 400 && (
              <button className="m-2 min-h-11 rounded-lg px-3 text-sm font-medium text-accent hover:bg-raised" onClick={() => setHorizon(400)}>
                {t('Visa längre fram', 'Show further ahead')}
              </button>
            )}
          </Panel>
        );
      })}

      {done.length > 0 && (
        <details className="rounded-[var(--radius-panel)] border border-border bg-panel px-2 py-1">
          <summary className="min-h-11 cursor-pointer px-3 py-3 text-sm font-semibold text-muted">
            {t(`Klara här (${done.length})`, `Done here (${done.length})`)}
          </summary>
          <ul>
            {done.map((e) => (
              <EntryRow key={e.item.uid} e={e} today={today} tz={tz} reviews={reviews} draftKeys={draftKeys} />
            ))}
          </ul>
        </details>
      )}
      <p className="text-xs text-muted">{tr('AutoDay hjälper dig hålla koll men kan inte garantera att ingen deadline missas. Kontrollera viktiga datum mot originalkällan.', 'AutoDay helps you keep track but cannot guarantee that no deadline is missed. Check important dates against the original source.')}</p>
    </div>
  );
}
